import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { createAgentClient } from "../api/agentClient";
import { useClinicalRuntime } from "../auth/ClinicalRuntimeProvider";
import { canImportEmrPage } from "./captureIntake";

/** The cached copy of whether this clinic offers the import; the admin page refreshes it. */
export function emrPageImportSettingKey(agentApiOrigin: string, siteId: string) {
  return ["emr-page-import-setting", agentApiOrigin, siteId] as const;
}

/**
 * Whether this member may register a patient from another EMR's chart page
 * here: the build offers it, they are a physician or clinic administrator, and
 * a clinic administrator has turned it on for this clinic. The workforce
 * profile and the clinic's setting are asked for only in builds that offer the
 * import; the profile shares the shell's cached copy.
 */
export function useEmrPageImportAccess(): {
  readonly allowed: boolean;
  readonly checking: boolean;
  /** The member could use it, but this clinic has it turned off. */
  readonly turnedOff: boolean;
} {
  const runtime = useClinicalRuntime();
  const enabled = runtime.config.emrPageImportEnabled === true;
  const { agentApiOrigin, siteId } = runtime.config;
  const getAccessToken = runtime.workforce.accessToken;
  const client = useMemo(
    () => createAgentClient({ baseUrl: agentApiOrigin, siteId, getAccessToken }),
    [agentApiOrigin, siteId, getAccessToken],
  );
  const profile = useQuery({
    queryKey: ["workforce-labels", agentApiOrigin, siteId],
    queryFn: ({ signal }) => client.getWorkforceProfile(signal),
    enabled,
  });
  const permitted = canImportEmrPage(runtime.config, profile.data?.role);
  const setting = useQuery({
    queryKey: emrPageImportSettingKey(agentApiOrigin, siteId),
    queryFn: ({ signal }) => client.getEmrPageImportSetting(siteId, signal),
    enabled: permitted,
    // Asked again whenever a page that offers the import opens, so an
    // administrator turning it off is heeded on the next page, not minutes later.
    staleTime: 0,
  });
  return {
    allowed: permitted && setting.data?.enabled === true,
    checking: enabled && (profile.isPending || (permitted && setting.isPending)),
    turnedOff: permitted && setting.data?.enabled === false,
  };
}
